import {
  attachEnvironmentDescriptor,
  createKnownEnvironment,
  environmentIsWorkspaceProxy,
  environmentSupportsCloudProjects,
  type KnownEnvironment,
} from "@cmmd-forge/client-runtime";
import type { EnvironmentId, ExecutionEnvironmentDescriptor } from "@cmmd-forge/contracts";
import { create } from "zustand";

import {
  BootstrapHttpError,
  readJsonResponse,
  retryTransientBootstrap,
} from "./forgeSessionClient";

import { readPrimaryEnvironmentTarget, resolvePrimaryEnvironmentHttpUrl } from "./target";
import { hydrateRuntimeBridgeBootstrapEnvelope } from "../runtimeBridge";

const SERVER_ENVIRONMENT_DESCRIPTOR_PATH = "/.well-known/forge/environment";

interface PrimaryEnvironmentBootstrapState {
  readonly descriptor: ExecutionEnvironmentDescriptor | null;
  readonly setDescriptor: (descriptor: ExecutionEnvironmentDescriptor | null) => void;
  readonly reset: () => void;
}

const usePrimaryEnvironmentBootstrapStore = create<PrimaryEnvironmentBootstrapState>()((set) => ({
  descriptor: null,
  setDescriptor: (descriptor) => set({ descriptor }),
  reset: () => set({ descriptor: null }),
}));

let primaryEnvironmentDescriptorPromise: Promise<ExecutionEnvironmentDescriptor> | null = null;

function createPrimaryKnownEnvironment(input: {
  readonly source: KnownEnvironment["source"];
  readonly target: KnownEnvironment["target"];
}): KnownEnvironment | null {
  const descriptor = readPrimaryEnvironmentDescriptor();
  if (!descriptor) {
    return null;
  }

  return attachEnvironmentDescriptor(
    createKnownEnvironment({
      id: descriptor.environmentId,
      label: descriptor.label,
      source: input.source,
      target: input.target,
    }),
    descriptor,
  );
}

async function fetchPrimaryEnvironmentDescriptor(): Promise<ExecutionEnvironmentDescriptor> {
  return retryTransientBootstrap(async () => {
    await hydrateRuntimeBridgeBootstrapEnvelope();
    const bearerToken = readPrimaryEnvironmentTarget()?.bearerToken;
    const endpointUrl = resolvePrimaryEnvironmentHttpUrl(SERVER_ENVIRONMENT_DESCRIPTOR_PATH);
    const response = bearerToken
      ? await fetch(endpointUrl, { headers: { Authorization: `Bearer ${bearerToken}` } })
      : await fetch(endpointUrl);
    if (!response.ok) {
      throw new BootstrapHttpError({
        message: `Failed to load server environment descriptor (${response.status}).`,
        status: response.status,
      });
    }

    const descriptor = await readJsonResponse<ExecutionEnvironmentDescriptor>(
      response,
      endpointUrl,
    );
    writePrimaryEnvironmentDescriptor(descriptor);
    return descriptor;
  });
}

export function readPrimaryEnvironmentDescriptor(): ExecutionEnvironmentDescriptor | null {
  return usePrimaryEnvironmentBootstrapStore.getState().descriptor;
}

export function usePrimaryEnvironmentId(): EnvironmentId | null {
  return usePrimaryEnvironmentBootstrapStore((state) => state.descriptor?.environmentId ?? null);
}

export function usePrimaryEnvironmentDescriptor(): ExecutionEnvironmentDescriptor | null {
  return usePrimaryEnvironmentBootstrapStore((state) => state.descriptor);
}

/**
 * The CMMD device runtime id the connected primary server reports for itself,
 * or null when it reports none.
 *
 * This is the machine's second name, the one the Run on chip and the runtime
 * inventory use. Reading it here rather than from page storage is what makes a
 * local desktop resolve as local in an ordinary browser: storage only holds the
 * desktop's id when the page runs inside the desktop shell, and otherwise holds
 * an id that browser minted for itself (FOR-36439).
 *
 * Null while the descriptor is still in flight, so subscribe with the hook on
 * any surface that can render before bootstrap finishes.
 */
export function usePrimaryEnvironmentDeviceRuntimeId(): string | null {
  return usePrimaryEnvironmentBootstrapStore((state) => state.descriptor?.deviceRuntimeId ?? null);
}

/** Imperative form of {@link usePrimaryEnvironmentDeviceRuntimeId}, for code
 *  that re-reads live state on every attempt rather than rendering. */
export function readPrimaryEnvironmentDeviceRuntimeId(): string | null {
  return readPrimaryEnvironmentDescriptor()?.deviceRuntimeId ?? null;
}

/**
 * True when the connected primary server can manage cloud project folders. New
 * descriptors report this explicitly; legacy descriptors fall back to
 * `capabilities.cloudMode`.
 */
export function usePrimaryEnvironmentCloudMode(): boolean {
  return usePrimaryEnvironmentBootstrapStore((state) =>
    environmentSupportsCloudProjects(state.descriptor),
  );
}

/**
 * True when the primary descriptor explicitly reports a workspace proxy.
 * A workspace proxy has no local workspace, so the SPA must not wait for a
 * workspace bootstrap snapshot.
 */
export function usePrimaryEnvironmentIsWorkspaceProxy(): boolean {
  return usePrimaryEnvironmentBootstrapStore((state) =>
    environmentIsWorkspaceProxy(state.descriptor),
  );
}

export function writePrimaryEnvironmentDescriptor(
  descriptor: ExecutionEnvironmentDescriptor | null,
): void {
  usePrimaryEnvironmentBootstrapStore.getState().setDescriptor(descriptor);
}

function descriptorMatchesHydratedBridge(
  descriptor: ExecutionEnvironmentDescriptor,
  envelope: Awaited<ReturnType<typeof hydrateRuntimeBridgeBootstrapEnvelope>>,
): boolean {
  if (envelope?.primaryMode !== "cloud") {
    return true;
  }
  return descriptor.environmentId === envelope.environmentId;
}

export function getPrimaryKnownEnvironment(): KnownEnvironment | null {
  const primaryTarget = readPrimaryEnvironmentTarget();
  if (!primaryTarget) {
    return null;
  }

  return createPrimaryKnownEnvironment({
    source: primaryTarget.source,
    target: primaryTarget.target,
  });
}

export function resolveInitialPrimaryEnvironmentDescriptor(): Promise<ExecutionEnvironmentDescriptor> {
  return resolveInitialPrimaryEnvironmentDescriptorAsync();
}

async function resolveInitialPrimaryEnvironmentDescriptorAsync(): Promise<ExecutionEnvironmentDescriptor> {
  const envelope = await hydrateRuntimeBridgeBootstrapEnvelope();
  const descriptor = readPrimaryEnvironmentDescriptor();
  if (descriptor && descriptorMatchesHydratedBridge(descriptor, envelope)) {
    return Promise.resolve(descriptor);
  }
  if (descriptor) {
    writePrimaryEnvironmentDescriptor(null);
  }

  if (primaryEnvironmentDescriptorPromise) {
    return primaryEnvironmentDescriptorPromise;
  }

  const nextPromise = fetchPrimaryEnvironmentDescriptor();
  primaryEnvironmentDescriptorPromise = nextPromise;
  return nextPromise.finally(() => {
    if (primaryEnvironmentDescriptorPromise === nextPromise) {
      primaryEnvironmentDescriptorPromise = null;
    }
  });
}

export function __resetPrimaryEnvironmentBootstrapForTests(): void {
  primaryEnvironmentDescriptorPromise = null;
  usePrimaryEnvironmentBootstrapStore.getState().reset();
}

export const resetPrimaryEnvironmentDescriptorForTests = __resetPrimaryEnvironmentBootstrapForTests;
